import type { Player } from '@mentimeter/quiz-types';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Bubbles } from '../Bubbles';
import { FadeIn, ScaleIn } from '../Transitions';

const MAX_AVATARS = 75;

const Bold = (chunks: ReactNode) => (
  <Text color="inherit" fontSize="inherit" fontWeight="semiBold">
    {chunks}
  </Text>
);

export const Lobby = ({
  players,
  current,
  total,
  connected,
}: {
  players: Player[];
  current: number;
  total: number;
  connected: boolean;
}) => {
  const translate = useTranslations('presenting');
  const {
    theme: { visualizationColors },
  } = useRagnar();

  const visiblePlayers = players.slice(0, MAX_AVATARS);
  const numberOfConnectedPlayers = players.length;
  const showCounter = total !== 0;

  return (
    <Box width="100%" height="100%" alignItems="center">
      {showCounter && (
        <FadeIn>
          <Text color="text" fontWeight="light" fontSize="500">
            {translate.rich('quiz.lobby.question_counter_message', {
              index: String(current),
              totalQuestions: String(total),
              bold: Bold,
            })}
          </Text>
        </FadeIn>
      )}

      {numberOfConnectedPlayers === 0 ? (
        <Box
          width="100%"
          height="100%"
          flex="1 1 auto"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="text" fontWeight="semiBold" fontSize="200">
            {translate('legacy.animating_lobby.waiting_players')}
          </Text>
        </Box>
      ) : (
        <>
          <Box width="100%" height="100%" flex="1 1 auto">
            <Bubbles
              players={players}
              colors={visualizationColors.fillColors}
              maxNodes={MAX_AVATARS}
              textColor={visualizationColors.textColor}
            />
          </Box>
          <Box alignItems="center">
            <ScaleIn>
              <Text
                textAlign="center"
                fontWeight="bold"
                fontSize="200"
                color="text"
              >
                {translate('quiz.lobby.player_count_ready_message', {
                  numberOfPlayers: numberOfConnectedPlayers,
                })}
              </Text>
            </ScaleIn>
            {numberOfConnectedPlayers > visiblePlayers.length && (
              <Box bg="text" py={2} px={3} borderRadius={2}>
                <Text textAlign="center" fontSize="125" color="bg">
                  {translate('quiz.lobby.maximum_visible_avatars_message', {
                    numberOfAvatars: MAX_AVATARS,
                  })}
                </Text>
              </Box>
            )}
          </Box>
        </>
      )}
      {!connected && (
        <Text color="text" fontSize="125">
          {translate('quiz.lobby.no_connection_message')}
        </Text>
      )}
    </Box>
  );
};
